import React from 'react';
import { isEmpty, camelCase, groupBy } from 'lodash';
import dayjs from './dates';
import Alert from './alert';
import { ERR_MSG } from '../globalConstants';

export const fieldFormat = (label: string, append: string): string => {
  if (!label) return `XXX${append}`;
  return `${label
    .replace(/[\s/]/g, '')
    .replace('$', 'D-')
    .replace('%', 'P-')
    .replace(/[()]/g, '')}${append}`;
};

export const createSelectOptions = (arr: any[]) => {
  if (isEmpty(arr)) {
    return [];
  }

  return arr.map(({ id, name, disabled }) => ({
    value: id,
    label: name,
    disabled,
  }));
};

export const addYearToDate = (date: string | null): string | null => {
  if (!date) {
    return null;
  }
  const jdate = new Date(`${date}\n`);
  jdate.setFullYear(jdate.getFullYear() + 1);
  const finalYear = jdate.getFullYear();
  const finalMonth = String(jdate.getMonth() + 1).padStart(2, '0');
  const finalDay = String(jdate.getDate()).padStart(2, '0');

  return `${finalYear}-${finalMonth}-${finalDay}`;
};

/**
 *
 * @param {*} date - string of form YYYY-MM-DD
 */
export const roundDownToWeekday = (date: string): string => {
  const jdate = new Date(`${date}\n`);
  let dayofweek = jdate.getDay();

  while (dayofweek == 0 || dayofweek == 6) {
    jdate.setDate(jdate.getDate() - 1);
    dayofweek = jdate.getDay();
  }
  const finalYear = jdate.getFullYear();
  const finalMonth = String(jdate.getMonth() + 1).padStart(2, '0');
  const finalDay = String(jdate.getDate()).padStart(2, '0');

  return `${finalYear}-${finalMonth}-${finalDay}`;
};

export const displayFileName = (name: string, maxLen = 16): string => {
  const nameArray = name.split(/[_-]/);
  nameArray.pop();
  const rName = nameArray.join(' ');
  return rName.length > maxLen ? `${rName.substring(0, maxLen)}...` : rName;
};

export const displayText = (
  text: string | null | undefined,
  maxLen = 32,
): { text: string; more: boolean } => {
  if (!text) {
    return { text: '', more: false };
  }

  const regex = /https?:\/\/\S+/g;
  const replaced = text.replace(regex, function (url) {
    return `<a href="${url}" target="_blank">${url}</a>`;
  });
  return replaced.length > maxLen
    ? { text: replaced.substring(0, maxLen), more: true }
    : { text: replaced, more: false };
};

export const errorMsg = (msg: any): string =>
  typeof msg === 'object'
    ? msg.content
    : `Oops, there was an error loading ${msg}`;

export const prettyPhone = (phoneNo = ''): string => {
  if (!phoneNo) {
    return '';
  }

  const noCountry = `${phoneNo}`.replace('+1', '');
  const numbers = `${noCountry}`.replace(/\D/g, '');
  const match = numbers.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (!match || match.length === 0) {
    return phoneNo;
  }

  const [, areaCode, prefix, lineNo] = match;
  return !areaCode || !prefix || !lineNo
    ? ''
    : `(${areaCode}) ${prefix}-${lineNo}`;
};

export const utcToLocal = (value: any) => dayjs.utc(value).local();

export const parseDateValue = (value: any) => {
  if (!value) return dayjs(NaN);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return dayjs(new Date(year, month - 1, day));
  }
  return dayjs.utc(value).local();
};

export const formatTime = (value: any, format = 'L'): string => {
  if (!value) return '';
  return parseDateValue(value).format(format);
};

export const newformatTime = (value: any, format = 'L'): string =>
  formatTime(value, format);

export const formatDate = (date: any): string => {
  return parseDateValue(date).format('YYYY/MM/DD');
};

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const formatCurrencyOnBlur = (value: string | number | null): string => {
  let val: any = 0;
  if (value != null) {
    // Coerce first — callers may pass a raw number from the API.
    val = String(value).replace(/[^0-9.]/g, '');
  }

  return currencyFormatter.format(val);
};

export const formatEIN = (val: string): string => {
  let newVal;
  const numbers = val.replace(/[^0-9]/g, '');
  if (numbers.length === 9) {
    newVal = `${numbers.substr(0, 2)}-${numbers.substr(2, 8)}`;
  }
  const formattedTaxID = newVal || val;
  return formattedTaxID;
};

export const sortByLabel = (
  a: { label: string },
  b: { label: string },
): number => (a.label.toUpperCase() < b.label.toUpperCase() ? -1 : 1);

export const required = (value: any): string | undefined =>
  value ? undefined : 'This field is required';

// used to validate date entrys so that field is required, but year cannot be greater than 2100 or less than 2000
export const dateValidate = (value: any): string | undefined => {
  if (!value) {
    return 'This field is required';
  }
  const date = new Date(value);
  // if date year is greater than 2100 or less than 2000 return message
  if (date.getFullYear() > 2100 || date.getFullYear() < 2000) {
    return 'Please enter a date between 2000 and 2100';
  }
  return undefined;
};

export const selectionRequired = (value: any): string | undefined =>
  value ? undefined : 'You must select an option';

export const atLeastOne = (value: any): string | undefined =>
  value && value.length > 0 ? undefined : 'You must select at least one option';

export const emailVal = (value: string): string | undefined => {
  if (!value || !value.trim()) return undefined;
  return value &&
    value.match(
      /[A-Za-z0-9!#$%&'*+-/=?^_`{|}~]+@[A-Za-z0-9-]+(.[A-Za-z0-9-]+)*/,
    )
    ? undefined
    : 'Email format incorrect';
};

export const passwordVal = (value: string): string | undefined =>
  value.length > 7 ? undefined : 'Password must be at least 8 characters long';

export const phoneNumberValidation = (
  phoneNumber = '',
): { phoneNumber: string; error?: string } => {
  const value: { phoneNumber: string; error?: string } = { phoneNumber };
  const noCountry = `${phoneNumber}`.replace('+1', '');
  const numbers = `${noCountry}`.replace(/\D/g, '');
  const match = numbers.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match || match.length === 0) {
    value.error =
      'Phone Number must be 10 digits including the area code (ie: 5558882222)';
    return value;
  }
  const [, areaCode, prefix, lineNo] = match;
  value.phoneNumber = `(${areaCode}) ${prefix}-${lineNo}`;
  return value;
};

export const phoneNumberVal = (num = ''): string | undefined => {
  if (!num || !num.trim()) return undefined;
  const noCountry = `${num}`.replace('+1', '');
  const numbers = `${noCountry}`.replace(/\D/g, '');
  const match = numbers.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match || match.length === 0)
    return 'Must be 10 digits including the area code (e.g., 5558882222)';
  return undefined;
};

export const taxIDVal = (num = ''): string | undefined => {
  const message =
    'Tax ID needs to be in the form of "XX-XXXXXXX" (e.g., 12-34567890)';
  let flag = false;

  if (!num) {
    return 'Field required';
  }
  const numArr = num.split('-');

  if (numArr.length == 1) {
    if (numArr[0].length != 9 || (parseInt(numArr[0]) as any) != numArr[0]) {
      flag = true;
    }
  } else {
    if (numArr[0].length != 2 || (parseInt(numArr[0]) as any) != numArr[0]) {
      flag = true;
    }
    if (numArr[1].length != 7 || (parseInt(numArr[1]) as any) != numArr[1]) {
      flag = true;
    }
  }

  if (flag) return message;
  return undefined;
};

export const composeValidators =
  (...validators: Array<(v: any) => any>) =>
  (value: any) =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined,
    );

export const isExpired = (val: any): boolean =>
  val && parseDateValue(val).diff(dayjs(), 'day') < 0;
export const findItemViaPathname = (data: any[], path: string) =>
  data && data.find(({ id }) => id === parseInt(path.split('/')[4], 10));

export const checkPermissions = (
  userPermissions: string[] | null | undefined,
  allowedPermissions: string[],
): boolean => {
  if (allowedPermissions.length === 0) {
    return true;
  }

  return (
    userPermissions &&
    userPermissions.some((permission) =>
      allowedPermissions.includes(permission),
    )
  );
};

export const catchPlanDetailError = (response: any) => {
  const message = response?.data?.message;
  const status = response?.status;
  return Alert.error(status === 403 ? message || ERR_MSG : ERR_MSG);
};
export const addStoText = (length: number): string => (length !== 1 ? 's' : '');

export const filterHelper = (
  data: any[],
  target: string,
  filter: any,
): number =>
  !isEmpty(data) ? data.filter((item) => item[target] === filter).length : 0;

export const CLOSED_STATUSES = [
  'Awarded',
  'Accepted',
  'Completed',
  'Declined',
  'Deferred',
  'Never Seek',
  'Withdrawn',
  'Pending',
];
// this returns values that are filtered against another array where the data exists
export const filterWhereExists = (
  data: any[],
  target: string,
  array: any[],
) => {
  if (!isEmpty(data)) {
    return data.filter((item) => (array.includes(item[target]) ? item : null));
  }
  return 0;
};

export const isClosed = (status: string): boolean =>
  CLOSED_STATUSES.includes(status);

const oneClient = (client: any[]) => {
  return client.reduce(
    (acc, project) => {
      const { status, amount_awarded, amount_requested, actual_hours } =
        project;

      const hours =
        typeof actual_hours === 'string'
          ? parseFloat(actual_hours)
          : actual_hours || 0;

      const amountAwarded =
        typeof amount_awarded === 'string'
          ? parseInt(amount_awarded, 10)
          : amount_awarded || 0;

      const amountRequested =
        typeof amount_requested === 'string'
          ? parseInt(amount_requested, 10)
          : amount_requested || 0;

      acc.projects.count += 1;
      acc.projects.amount += amountAwarded;
      acc.hours += hours;
      const amount = status === 'Awarded' ? amountAwarded : amountRequested;

      if (!acc[camelCase(status)]) {
        acc[camelCase(status)] = { count: 0, amount: 0 };
      }

      acc[camelCase(status)] = {
        count: acc[camelCase(status)].count + 1,
        amount: acc[camelCase(status)].amount + amount,
      };

      return acc;
    },
    {
      hours: 0,
      cultivating: { count: 0, amount: 0 },
      planned: { count: 0, amount: 0 },
      preliminary: { count: 0, amount: 0 },
      loiPending: { count: 0, amount: 0 },
      working: { count: 0, amount: 0 },
      hotMess: { count: 0, amount: 0 },
      goNoGo: { count: 0, amount: 0 },
      accepted: { count: 0, amount: 0 },
      projects: { count: 0, amount: 0 },
      awarded: { count: 0, amount: 0 },
      completed: { count: 0, amount: 0 },
      declined: { count: 0, amount: 0 },
      deferred: { count: 0, amount: 0 },
      neverSeek: { count: 0, amount: 0 },
      withdrawn: { count: 0, amount: 0 },
      pending: { count: 0, amount: 0 },
    },
  );
};

const multipleClients = (clients: any[][]) => {
  return clients.reduce(
    (acc: any, client: any) => {
      const [[, hours], projects, ...closed]: any[] = Object.entries(
        oneClient(client),
      );

      const totaled = ([, { count, amount }]: any, key: string) => ({
        count: count + acc[key].count,
        amount: amount + acc[key].amount,
      });

      closed.forEach((item: any) => {
        const [name] = item;
        acc[name] = totaled(item, name);
      });

      acc.projects = totaled(projects, 'projects');
      acc.hours += hours;

      return acc;
    },
    {
      hours: 0,
      cultivating: { count: 0, amount: 0 },
      planned: { count: 0, amount: 0 },
      preliminary: { count: 0, amount: 0 },
      loiPending: { count: 0, amount: 0 },
      working: { count: 0, amount: 0 },
      hotMess: { count: 0, amount: 0 },
      goNoGo: { count: 0, amount: 0 },
      accepted: { count: 0, amount: 0 },
      projects: { count: 0, amount: 0 },
      awarded: { count: 0, amount: 0 },
      completed: { count: 0, amount: 0 },
      declined: { count: 0, amount: 0 },
      deferred: { count: 0, amount: 0 },
      neverSeek: { count: 0, amount: 0 },
      withdrawn: { count: 0, amount: 0 },
      pending: { count: 0, amount: 0 },
    },
  );
};

export const aggregateProjects = (input: any[]) => {
  const groupedClients = Object.values(groupBy(input, 'name')) as any[][];
  if (groupedClients.length > 1) {
    return multipleClients(groupedClients);
  }
  return oneClient(input);
};

export const aggregateDeadlines = (deadlines: any[]) => {
  const totalDeadlines = deadlines.length;

  const { actions, projects } = deadlines.reduce(
    (acc, deadline) =>
      deadline.title
        ? { ...acc, actions: acc.actions + 1 }
        : { ...acc, projects: acc.projects + 1 },
    { actions: 0, projects: 0 },
  );

  return { actions, projects, totalDeadlines };
};

export const getSelectOptions = (values: Array<{ value: any; id: any }>) =>
  values.map(({ value, id }) => ({ label: value, value: id }));

export const paginateArray = (arr: any[], size = 10): any[][] => {
  const result = [];

  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }

  return result;
};

export const limitString = (value: string, length: number): string =>
  value.length >= length ? `${value.slice(0, length - 3)}...` : value;

// Utility to render multiline text with line breaks for web
export function renderMultilineText(inputText: string | null | undefined) {
  if (!inputText) return '';
  return inputText
    .split('\n')
    .map((line, idx, arr) =>
      idx < arr.length - 1
        ? [line, <br key={line ? `${line}-${idx}` : idx} />]
        : line,
    );
}
